import {
  createOrvalRequestOptions,
  savePortalOnboarding,
  startPortalKyc,
  type KycVerificationDto,
  type OnboardingDto,
  type OnboardingRequestDto,
  type OnboardingStep,
} from "@token/shared";
import type { ApiClient } from "@/lib/api";
import type { KycOnboardingMethod, OnboardingApi, OnboardingFormModel } from "./types";

const MOCK_STORAGE_KEY = "portal-onboarding-draft";

export type OnboardingApiMode = "http" | "mock";

export interface CreateOnboardingApiOptions {
  mode?: OnboardingApiMode;
}

export function createOnboardingApi(api: ApiClient, opts: CreateOnboardingApiOptions = {}): OnboardingApi {
  if (opts.mode === "mock") {
    return new MockOnboardingApi();
  }
  return new HttpOnboardingApi(api);
}

class HttpOnboardingApi implements OnboardingApi {
  constructor(private readonly api: ApiClient) {}

  async saveStep(step: OnboardingStep, data: OnboardingFormModel): Promise<OnboardingFormModel> {
    const response = await savePortalOnboarding(toBackendPayload(data), { step }, createOrvalRequestOptions(this.api));
    return toOnboardingDraft(response.data, data);
  }

  async startKyc(method: KycOnboardingMethod): Promise<KycVerificationDto> {
    const response = await startPortalKyc({ kycMethod: method }, createOrvalRequestOptions(this.api));
    return response.data;
  }
}

class MockOnboardingApi implements OnboardingApi {
  async saveStep(_step: OnboardingStep, data: OnboardingFormModel): Promise<OnboardingFormModel> {
    sessionStorage.setItem(MOCK_STORAGE_KEY, JSON.stringify(data));
    await new Promise((resolve) => window.setTimeout(resolve, 140));
    return data;
  }

  async startKyc(_method: KycOnboardingMethod): Promise<KycVerificationDto> {
    await new Promise((resolve) => window.setTimeout(resolve, 140));
    return { status: "PENDING" as KycVerificationDto["status"] };
  }
}

function toBackendPayload(data: OnboardingFormModel): OnboardingRequestDto {
  return {
    id: stringOrUndefined(data.id),
    salutation: salutationCode(data.salutation),
    titleBefore: data.titleBefore,
    titleAfter: data.titleAfter,
    firstName: data.firstName,
    lastName: data.lastName,
    birthDate: data.birthDate,
    nationality: data.nationality,
    birthPlace: data.birthPlace,
    streetName: data.streetName,
    houseNumber: data.houseNumber,
    postCode: data.postCode,
    city: data.city,
    country: data.country,
    mobileNumber: data.mobileNumber,
    pep: data.pep,
    safekeeping: data.safekeeping,
    agb: data.agb,
    onOwnAccount: data.onOwnAccount,
    status: data.status,
  };
}

function toOnboardingDraft(data: OnboardingDto, fallback: OnboardingFormModel): OnboardingFormModel {
  return {
    ...fallback,
    ...data,
    id: data.id ?? fallback.id,
    customerId: data.customerId ?? fallback.customerId,
    salutation: salutationLabel(data.salutation ?? fallback.salutation),
    titleBefore: data.titleBefore ?? fallback.titleBefore,
    titleAfter: data.titleAfter ?? fallback.titleAfter,
    firstName: data.firstName ?? fallback.firstName,
    lastName: data.lastName ?? fallback.lastName,
    birthDate: data.birthDate ?? fallback.birthDate,
    nationality: data.nationality ?? fallback.nationality,
    birthPlace: data.birthPlace ?? fallback.birthPlace,
    streetName: data.streetName ?? fallback.streetName,
    houseNumber: data.houseNumber ?? fallback.houseNumber,
    postCode: data.postCode ?? fallback.postCode,
    city: data.city ?? fallback.city,
    country: data.country ?? fallback.country,
    mobileNumber: data.mobileNumber ?? fallback.mobileNumber,
    pep: data.pep ?? fallback.pep,
    safekeeping: data.safekeeping ?? fallback.safekeeping,
    agb: fallback.agb,
    onOwnAccount: data.onOwnAccount ?? fallback.onOwnAccount,
    status: data.status ?? fallback.status,
  };
}

function stringOrUndefined(value: string | number | null): string | undefined {
  return value === null ? undefined : String(value);
}

function salutationCode(value: string): string {
  if (value === "Herr") return "M";
  if (value === "Frau") return "F";
  if (value === "Divers") return "D";
  return value;
}

function salutationLabel(value: string): string {
  if (value === "M") return "Herr";
  if (value === "F") return "Frau";
  if (value === "D") return "Divers";
  return value;
}
